/**
 * ProfileDropdown — signed-in user's floating profile card, extracted from
 * TopBar in App.js (iter-44). Fully self-contained: owns its own editing/
 * avatar-upload/referrals/share state. Parent renders it conditionally when
 * the user menu button is clicked; onClose fires whenever the seeker takes
 * an action that should close the card (logout, open subscription, etc.).
 *
 * All data-testids from the previous inline version are preserved so
 * existing testing_agent flows keep passing.
 */
import React, { useContext, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  User, LogOut, Camera, Lock, CreditCard, Share2, Briefcase,
  Sparkles, ChevronDown, Copy, Send,
} from "lucide-react";
import { AuthContext } from "@/context/AuthContext";
import { EnlightenmentContext } from "@/context/EnlightenmentContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileDropdown = ({ onClose, onOpenSubscription, onOpenShareSheet }) => {
  const navigate = useNavigate();
  const { user, logout, updateProfile } = useContext(AuthContext);
  const { sessionId, credits, freeInteractionsLeft, accessReason, openPaywall } = useContext(EnlightenmentContext);

  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [referralCount, setReferralCount] = useState(0);
  const [referralStatus, setReferralStatus] = useState(null);
  const [referralPanelOpen, setReferralPanelOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Load referral counts + lifetime price ladder when the panel mounts.
  useEffect(() => {
    if (!user || !sessionId) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/referral/status/${sessionId}`, {
          credentials: "include", cache: "no-store",
        });
        if (r.ok) {
          const d = await r.json();
          setReferralCount(d.referral_count || 0);
          setReferralStatus(d);
        }
      } catch { /* non-critical */ }
    })();
  }, [user, sessionId]);

  const handleShareSite = async () => {
    if (!user) return;
    const refCode = user.referral_code || user.email;
    if (!refCode) return;
    const shareUrl = `${window.location.origin}/?ref=${encodeURIComponent(refCode)}`;
    const shareText = `Walking the path to Primordeia with Papaya, the white rabbit. Use my code so we both gain — ${shareUrl}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Primordeia", text: shareText, url: shareUrl }); return; }
      catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2400);
    } catch (err) { console.warn("[Share] clipboard blocked:", err?.message || err); }
  };

  const copyRefCode = async () => {
    if (!user?.referral_code) return;
    try {
      await navigator.clipboard.writeText(user.referral_code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2400);
    } catch (err) { console.warn("[Share] copy code failed:", err?.message || err); }
  };

  const copyShareLink = async () => {
    if (!user) return;
    const refCode = user.referral_code || user.email;
    if (!refCode) return;
    const shareUrl = `${window.location.origin}/?ref=${encodeURIComponent(refCode)}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2400);
    } catch (err) { console.warn("[Share] copy link failed:", err?.message || err); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert("Image must be under 2MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE}/auth/upload-avatar`, {
        method: "POST", credentials: "include", body: formData,
      });
      if (res.ok) {
        const d = await res.json();
        updateProfile({ profile_pic: d.profile_pic });
      }
    } catch { /* upload non-critical */ } finally { setUploading(false); }
  };

  const handleSaveName = async () => {
    if (editName.trim() && editName !== user?.name) {
      await updateProfile({ name: editName.trim() });
    }
    setEditingName(false);
  };

  const setSecurityQuestion = async () => {
    const q = prompt("Set your security question (e.g., \"What is your pet's name?\"):");
    if (!q) return;
    const a = prompt("Set your answer:");
    if (!a) return;
    await fetch(`${API_BASE}/auth/set-security-question`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      credentials: "include", body: JSON.stringify({ question: q, answer: a }),
    });
    alert("Security question set! You can use this to recover your password.");
  };

  if (!user) return null;

  return (
    <div
      className="absolute right-0 top-10 bg-white rounded-xl shadow-xl p-4 min-w-[240px] z-50"
      style={{ border: "1px solid #E8E0F0" }}
      data-testid="profile-dropdown"
    >
      {/* Avatar */}
      <div className="flex flex-col items-center mb-3">
        <div className="relative group cursor-pointer mb-2" onClick={() => fileInputRef.current?.click()}>
          {user.profile_pic ? (
            <img src={user.profile_pic} alt="" className="w-16 h-16 rounded-full object-cover border-2" style={{ borderColor: "#E8E0F0" }} />
          ) : (
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: "#F0E8F8" }}>
              <User size={28} style={{ color: "#4F378B" }} />
            </div>
          )}
          <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera size={18} style={{ color: "#FFF" }} />
          </div>
          {uploading && (
            <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
          onChange={handleAvatarUpload} data-testid="avatar-upload-input" />
        <span className="text-[10px]" style={{ color: "#8B7DA8" }}>Click to change photo</span>
      </div>

      {/* Name */}
      {editingName ? (
        <div className="flex gap-1 mb-2">
          <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
            className="flex-1 px-2 py-1 text-xs rounded-lg border" style={{ borderColor: "#E8E0F0" }}
            autoFocus onKeyDown={e => e.key === "Enter" && handleSaveName()}
            data-testid="edit-name-input" />
          <button onClick={handleSaveName} className="px-2 py-1 rounded-lg text-[10px] font-semibold text-white"
            style={{ backgroundColor: "#4F378B" }} data-testid="save-name-button">Save</button>
        </div>
      ) : (
        <button onClick={() => { setEditName(user.name || ""); setEditingName(true); }}
          className="w-full text-center text-xs font-semibold mb-1 hover:underline" style={{ color: "#4F378B" }}
          data-testid="edit-name-trigger">
          {user.name || "Set your name"}
        </button>
      )}

      <div className="text-[10px] text-center mb-3" style={{ color: "#8B7DA8" }}>{user.email}</div>

      {/* Lotus credit balance */}
      <button
        onClick={() => { openPaywall(); onClose(); }}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl mb-2 transition hover:scale-[1.01]"
        style={{
          background: accessReason === "exempt" || accessReason === "lifetime"
            ? "linear-gradient(135deg, rgba(76,175,80,0.15), rgba(123,94,167,0.18))"
            : "linear-gradient(135deg, rgba(212,165,116,0.18), rgba(123,94,167,0.18))",
          border: "1px solid rgba(212,165,116,0.35)",
        }}
        title="Tap to add more lotuses"
        data-testid="profile-lotus-counter"
      >
        <div className="flex items-center gap-2">
          <span className="text-xl" style={{ filter: "drop-shadow(0 0 6px rgba(212,165,116,0.7))" }}>{"\u{1FAB7}"}</span>
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#7B5EA7" }}>Lotus balance</div>
            <div className="text-sm font-bold" style={{ color: "#4F378B", fontFamily: "'Fredoka', sans-serif" }}>
              {accessReason === "exempt" && "Unlimited (dev)"}
              {accessReason === "lifetime" && "Unlimited (lifetime)"}
              {accessReason === "subscribed" && "Unlimited (subscribed)"}
              {accessReason === "free" && `${freeInteractionsLeft} free + ${credits}`}
              {accessReason === "paid" && `${credits} lotus${credits === 1 ? "" : "es"}`}
              {accessReason === "paywall" && "0 — add more"}
            </div>
          </div>
        </div>
        {!(accessReason === "exempt" || accessReason === "lifetime" || accessReason === "subscribed") && (
          <div className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: "#4F378B", color: "#FFF" }}>+ Add</div>
        )}
      </button>

      <div className="border-t pt-2 space-y-1" style={{ borderColor: "#E8E0F0" }}>
        <div className="flex items-stretch gap-1">
          <button onClick={handleShareSite}
            className="flex-1 flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg hover:bg-gray-50"
            style={{ color: "#4F378B" }}
            data-testid="share-site-button">
            <Share2 size={12} /> {shareCopied ? "Link copied!" : "Share Primordeia (your referral link)"}
          </button>
          <button type="button" onClick={onOpenShareSheet}
            title="Choose a channel — each link is tagged so you can later see where your friends came from"
            className="px-2 py-1.5 rounded-lg hover:bg-gray-50 flex items-center"
            style={{ color: "#4F378B" }}
            data-testid="share-channels-button"
            aria-label="Open share channel menu">
            <Send size={12} />
          </button>
        </div>

        {/* Referrals panel */}
        <div className="mx-1 mt-1 rounded-xl overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(212,165,116,0.10), rgba(123,94,167,0.08))",
            border: "1px solid rgba(212,165,116,0.30)" }}
          data-testid="referrals-panel">
          <button type="button" onClick={() => setReferralPanelOpen(v => !v)}
            className="w-full flex items-center justify-between px-2.5 py-2 text-[11px] font-semibold"
            style={{ color: "#4F378B" }}
            data-testid="referrals-panel-toggle"
            aria-expanded={referralPanelOpen}>
            <span className="flex items-center gap-1.5">
              <Sparkles size={12} style={{ color: "#D4A574" }} />
              Referrals
              {referralCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                  style={{ background: "#D4A574", color: "#1A1030" }}>
                  {referralCount}
                </span>
              )}
            </span>
            <motion.span animate={{ rotate: referralPanelOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <ChevronDown size={12} style={{ color: "#7B5EA7" }} />
            </motion.span>
          </button>
          <AnimatePresence initial={false}>
            {referralPanelOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.22 }} className="overflow-hidden">
                <div className="px-2.5 pb-2.5 space-y-2">
                  <div className="grid grid-cols-2 gap-1.5" data-testid="referrals-stats">
                    <div className="rounded-lg p-1.5 text-center" style={{ background: "rgba(255,255,255,0.55)" }}>
                      <div className="text-[18px] font-bold leading-none" style={{ color: "#4F378B" }}
                        data-testid="referrals-friends-count">{referralCount}</div>
                      <div className="text-[8.5px] uppercase tracking-wider mt-0.5" style={{ color: "#7B5EA7" }}>
                        friend{referralCount === 1 ? "" : "s"} joined
                      </div>
                    </div>
                    <div className="rounded-lg p-1.5 text-center" style={{ background: "rgba(255,255,255,0.55)" }}>
                      <div className="text-[18px] font-bold leading-none" style={{ color: "#4F378B" }}
                        data-testid="referrals-months-count">{referralCount}</div>
                      <div className="text-[8.5px] uppercase tracking-wider mt-0.5" style={{ color: "#7B5EA7" }}>
                        free month{referralCount === 1 ? "" : "s"}
                      </div>
                    </div>
                    {referralStatus?.practitioner_referral_count > 0 && (
                      <div className="rounded-lg p-1.5 text-center col-span-2"
                        style={{ background: "rgba(212,165,116,0.20)", border: "1px solid rgba(212,165,116,0.45)" }}
                        data-testid="referrals-practitioner-count">
                        <div className="text-[12px] font-bold" style={{ color: "#4F378B" }}>
                          ✦ {referralStatus.practitioner_referral_count} practitioner referral{referralStatus.practitioner_referral_count === 1 ? "" : "s"}
                        </div>
                        <div className="text-[8.5px] mt-0.5 italic" style={{ color: "#7B5EA7" }}>
                          deepest lifetime discount unlocked
                        </div>
                      </div>
                    )}
                  </div>
                  {referralStatus && (
                    <div className="rounded-lg px-2 py-1.5 text-[10px] flex items-center justify-between"
                      style={{ background: "rgba(255,255,255,0.45)" }}
                      data-testid="referrals-lifetime-price">
                      <span style={{ color: "#5C5470" }}>your lifetime price</span>
                      <span className="font-bold" style={{ color: referralStatus.has_practitioner_referral ? "#1B5E20" : referralStatus.has_discount ? "#4F378B" : "#7B5EA7" }}>
                        ${Number(referralStatus.lifetime_price).toFixed(2)}
                        {referralStatus.has_practitioner_referral ? " · ✦ best" : referralStatus.has_discount ? " · ✓ unlocked" : ""}
                      </span>
                    </div>
                  )}
                  {user?.referral_code && (
                    <button onClick={copyRefCode}
                      className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-white/40 transition"
                      style={{ background: "rgba(255,255,255,0.55)" }}
                      data-testid="referrals-copy-code">
                      <span className="text-[10px] italic" style={{ color: "#5C5470" }}>your code</span>
                      <span className="flex items-center gap-1 font-mono font-bold tracking-wider text-[11px]"
                        style={{ color: "#4F378B" }}>
                        {codeCopied ? "✓ copied" : (<><span>{user.referral_code}</span><Copy size={10} /></>)}
                      </span>
                    </button>
                  )}
                  <button onClick={copyShareLink}
                    className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-white/40 transition"
                    style={{ background: "rgba(255,255,255,0.55)" }}
                    data-testid="referrals-copy-link">
                    <span className="text-[10px] italic" style={{ color: "#5C5470" }}>share link</span>
                    <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: "#4F378B" }}>
                      {linkCopied ? "✓ copied" : (<><span>copy</span><Copy size={10} /></>)}
                    </span>
                  </button>
                  <p className="text-[9.5px] italic leading-snug mt-1" style={{ color: "#7B5EA7" }}>
                    {referralCount > 0
                      ? <>Lifetime discount <strong>unlocked</strong>. Earn <strong>1 more free month</strong> for each additional friend who joins. Practitioner referrals drop your lifetime price to <strong>$59.99</strong>.</>
                      : <>Earn <strong>1 free month</strong> per friend who joins, unlock your lifetime discount on the very first, and drop to <strong>$59.99</strong> when a practitioner signs up using your code.</>}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {user?.is_practitioner && (
          <button onClick={() => { navigate("/practitioner"); onClose(); }}
            className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg hover:bg-gray-50"
            style={{ color: "#4F378B" }}
            data-testid="practitioner-dashboard-link">
            <Briefcase size={12} /> Practitioner Dashboard
          </button>
        )}
        <button onClick={() => { onOpenSubscription(); onClose(); }}
          className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg hover:bg-gray-50"
          style={{ color: "#4F378B" }}
          data-testid="my-subscription-button">
          <CreditCard size={12} /> My Subscription
        </button>
        <button onClick={setSecurityQuestion}
          className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg hover:bg-gray-50"
          style={{ color: "#4F378B" }}
          data-testid="set-security-question">
          <Lock size={12} /> Set Security Question
        </button>
        <button onClick={() => { logout(); onClose(); }}
          className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg hover:bg-gray-50"
          style={{ color: "#E74C3C" }}
          data-testid="logout-button">
          <LogOut size={12} /> Sign Out
        </button>
      </div>
    </div>
  );
};

export default ProfileDropdown;
